'use client';

import { useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import PostCard from './PostCard';
import AdCard from './AdCard';
import FactCard from './FactCard';
import { Loader2 } from 'lucide-react';
import React from 'react';

interface InfinitePostListProps {
    initialPosts: any[];
    initialTotal: number;
    fetchAction: (page: number, limit: number, search?: string, category?: string) => Promise<{ posts: any[]; total: number; totalPages: number }>;
    categoryLabel?: string;
    currentSearch?: string;
    currentCategory?: string;
    enableEdit?: boolean;
    fallbackAction?: (page: number, limit: number, search?: string, category?: string) => Promise<{ posts: any[]; total: number; totalPages: number }>;
    fallbackLabel?: string;
    baseUrl?: string;
    initialPage?: number;
}

export default function InfinitePostList({
    initialPosts,
    initialTotal,
    fetchAction,
    categoryLabel,
    currentSearch,
    currentCategory,
    className,
    enableEdit,
    fallbackAction,
    fallbackLabel = "More Articles",
    baseUrl = "/articles",
    initialPage = 1
}: InfinitePostListProps & { className?: string }) {
    const [posts, setPosts] = useState(initialPosts);
    const [page, setPage] = useState(initialPage);
    const [hasMore, setHasMore] = useState(posts.length < initialTotal);
    const [loading, setLoading] = useState(false);

    // Fallback State
    const [isFallback, setIsFallback] = useState(false);
    // Track unique IDs to prevent duplicates during fallback mixing
    const [seenIds, setSeenIds] = useState<Set<string>>(new Set(initialPosts.map(p => p.id)));

    const [adIndices, setAdIndices] = useState<number[]>([]);
    const [factIndices, setFactIndices] = useState<number[]>([]);

    // Helper to generate next ad index
    const getNextAdGap = () => Math.floor(Math.random() * (20 - 8 + 1)) + 8;

    // Helper to generate next fact index
    const getNextFactGap = () => Math.floor(Math.random() * (35 - 20 + 1)) + 20;

    // Initialize/Reset State
    useEffect(() => {
        setPosts(initialPosts);
        setPage(initialPage);
        setHasMore(initialPosts.length < initialTotal);
        setIsFallback(false);
        setSeenIds(new Set(initialPosts.map(p => p.id)));

        // Reset and Calculate initial ad positions
        const indices: number[] = [];
        let nextIndex = getNextAdGap();
        while (nextIndex < initialPosts.length) {
            indices.push(nextIndex);
            nextIndex += getNextAdGap();
        }
        setAdIndices(indices);

        // Reset and Calculate initial fact positions
        const fIndices: number[] = [];
        let nextFactIndex = getNextFactGap();
        while (nextFactIndex < initialPosts.length) {
            fIndices.push(nextFactIndex);
            nextFactIndex += getNextFactGap();
        }
        setFactIndices(fIndices);
    }, [initialPosts, initialTotal]);


    const { ref, inView } = useInView({
        threshold: 0,
        rootMargin: '200px',
    });

    useEffect(() => {
        if (inView && hasMore && !loading) {
            loadMore();
        }
    }, [inView, hasMore, loading]);

    const loadMore = async () => {
        setLoading(true);
        try {
            let nextPage = page + 1;
            let action = fetchAction;

            // Should we switch to Fallback?
            const actuallyIsFallback = isFallback; // Capture current state for this run
            if (actuallyIsFallback && fallbackAction) {
                action = fallbackAction;
            }

            // Arguments: If fallback, we likely want NO search/category filters?
            // Usually "More Articles" means "General Latest News".
            // So pass undefined for search/category if in fallback mode.
            const s = actuallyIsFallback ? undefined : currentSearch;
            const c = actuallyIsFallback ? undefined : currentCategory;

            const res = await action(nextPage, 12, s, c);

            if (res.posts.length > 0) {
                // Filter duplicates BEFORE setPosts so we can use it for analytics
                const uniqueNewPosts = res.posts.filter((p: any) => !seenIds.has(p.id));

                if (uniqueNewPosts.length === 0) {
                    // All duplicates, nothing to add
                    setLoading(false);
                    return;
                }

                setPosts((prev) => {
                    // Update seenIds
                    const nextSeen = new Set(seenIds);
                    uniqueNewPosts.forEach((p: any) => nextSeen.add(p.id));
                    setSeenIds(nextSeen);

                    const newPosts = [...prev, ...uniqueNewPosts];

                    // Recalculate Ad/Fact indices for new length
                    // (Logic omitted for brevity, reusing existing function scope if possible or copying logic)
                    // We need to implement the index logic here same as before
                    setAdIndices(prevIndices => {
                        const newIndices = [...prevIndices];
                        let lastIndex = newIndices.length > 0 ? newIndices[newIndices.length - 1] : 0;
                        let nextTarget = lastIndex + getNextAdGap();
                        while (nextTarget < newPosts.length) {
                            newIndices.push(nextTarget);
                            lastIndex = nextTarget;
                            nextTarget += getNextAdGap();
                        }
                        return newIndices;
                    });
                    setFactIndices(prevIndices => {
                        const newIndices = [...prevIndices];
                        let lastIndex = newIndices.length > 0 ? newIndices[newIndices.length - 1] : 0;
                        let nextTarget = lastIndex + getNextFactGap();
                        while (nextTarget < newPosts.length) {
                            newIndices.push(nextTarget);
                            lastIndex = nextTarget;
                            nextTarget += getNextFactGap();
                        }
                        return newIndices;
                    });

                    return newPosts;
                });

                setPage(nextPage);

                // Analytics (GA4: View Item List)
                import('@next/third-parties/google').then(({ sendGAEvent }) => {
                    const gaItems = uniqueNewPosts.map((p: any) => ({
                        item_id: p.id,
                        item_name: p.title,
                        item_category: p.category_name,
                    }));

                    sendGAEvent('event', 'view_item_list', {
                        item_list_id: currentCategory || 'feed',
                        item_list_name: `${categoryLabel || 'Latest News'} - Page ${nextPage}`,
                        items: gaItems
                    });
                });

                // Check if we reached end of CURRENT list
                if (posts.length + res.posts.length >= res.total) {
                    // If we are NOT yet in fallback mode, and we have a fallback action
                    if (!actuallyIsFallback && fallbackAction) {
                        console.log("Switching to Fallback Feed...");
                        setIsFallback(true);
                        setPage(0); // Reset page for fallback (next tick will become 1)
                        setHasMore(true); // Keep going!
                    } else {
                        // Truly the end (or end of fallback)
                        setHasMore(false);
                    }
                }
            } else {
                // Zero posts returned.
                if (!actuallyIsFallback && fallbackAction) {
                    setIsFallback(true);
                    setPage(0);
                    setHasMore(true);
                } else {
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Failed to load more posts:', error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full">

            {posts.length > 0 ? (
                <div className={className || "flex flex-col gap-8 sm:gap-12 w-auto -mx-4 sm:mx-0 sm:w-full mb-16"}>
                    {posts.map((post, index) => {
                        const showAdAfter = adIndices.includes(index);
                        const showFactAfter = factIndices.includes(index);
                        const isFullWidth = (index + 1) % 5 === 0;

                        // Check if this is the start of the fallback section?
                        // Hard to pinpoint exact index without tracking "fallbackStartIndex".
                        // Assuming simple append for now.

                        return (
                            <React.Fragment key={`${post.id}-${index}`}>
                                <PostCard
                                    post={post}
                                    categoryLabel={categoryLabel}
                                    className={isFullWidth ? "md:col-span-2" : ""}
                                    enableEdit={enableEdit}
                                />
                                {showAdAfter && <AdCard />}
                                {showFactAfter && <FactCard />}
                            </React.Fragment>
                        );
                    })}
                </div>
            ) : (
                <div className="text-center py-20 bg-[var(--bg-elevated)] rounded-xl border border-[var(--color-border)]">
                    <h3 className="text-xl font-bold mb-2">No available posts</h3>
                    <p className="text-[var(--color-text-muted)]">Check back later for new content.</p>
                </div>
            )}

            {/* Separator if Fallback Active */}
            {isFallback && hasMore && (
                <div className="relative py-8 mb-8">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-[var(--color-border)]"></div>
                    </div>
                    <div className="relative flex justify-center">
                        <span className="bg-[var(--bg-base)] px-4 text-sm font-bold text-[var(--color-text-muted)] uppercase tracking-widest">
                            {fallbackLabel}
                        </span>
                    </div>
                </div>
            )}

            {/* Loading / Sentinel */}
            {hasMore && (
                <div ref={ref} className="flex flex-col items-center justify-center py-12 gap-6">
                    {loading ? (
                        <div className="flex items-center gap-2 text-[var(--color-primary)]">
                            <Loader2 className="animate-spin" size={24} />
                            <span className="font-medium">Loading {isFallback ? 'more stories' : 'more articles'}...</span>
                        </div>
                    ) : (
                        <>
                            <div className="h-4 w-full" />
                            {/* SEO Ghost Pagination: Physically crawlable link pushed down by endless scroll */}
                            <a 
                                href={`${baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl}/page/${initialPage + 1}${currentSearch ? `?q=${currentSearch}` : ''}`} 
                                className="opacity-0 w-0 h-0 absolute overflow-hidden pointer-events-none"
                                aria-hidden="true"
                            >
                                Next Page
                            </a>
                        </>
                    )}
                </div>
            )}

            {!hasMore && posts.length > 0 && (
                <div className="text-center py-12 text-[var(--color-text-muted)] border-t border-[var(--color-border)] mt-8">
                    You've reached the end of the list.
                </div>
            )}
        </div>
    );
}

